import { useQueries } from '@tanstack/react-query'

import api from 'src/constants/api'

import useAccount from 'src/hooks/utils/useAccount'

export interface SubscriptionDiscount {
  subscription_id: string
  current_nce_discount: string
}

// Keys for react-query
const discountKeys = {
  all: ['discounts'] as const,
  byAccount: (accountId: string) => [...discountKeys.all, accountId] as const
}

// Fetch function for discounts
export const fetchDiscounts = async (accountId: string, signal?: AbortSignal): Promise<SubscriptionDiscount[]> => {
  if (!accountId) throw new Error('Account ID is missing')

  const response = await api.get(`/servicenow/account/${accountId}/discounts`, { signal })
  return response.data as SubscriptionDiscount[]
}

// Hook to fetch all discounts for an account
export const useDiscountsQuery = () => {
  const account = useAccount()

  return useQueries({
    queries: [
      {
        queryKey: discountKeys.byAccount(account.id),
        queryFn: ({ signal }: { signal?: AbortSignal }) => fetchDiscounts(account.id, signal),
        enabled: !!account.id,
        retry: false,
        retryOnMount: false,
        refetchOnWindowFocus: false,
        // Cache for 5 minutes since discounts don't change frequently
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000
      }
    ]
  })
}

// Create a map of subscription ID to discount for easy lookup
export const createDiscountMap = (discounts: SubscriptionDiscount[]): Record<string, number> => {
  const discountMap: Record<string, number> = {}

  discounts.forEach((discount) => {
    const discountValue = parseFloat(discount.current_nce_discount) || 0
    if (discountValue > 0) {
      discountMap[discount.subscription_id] = discountValue
    }
  })

  return discountMap
}

export default useDiscountsQuery
